import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  RotateCcw, Check, X, Eye, Package, Clock
} from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminReturns = () => {
  const { getAuthHeaders } = useAdmin();
  const [returns, setReturns] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [decisionNotes, setDecisionNotes] = useState('');

  useEffect(() => {
    fetchReturns();
    fetchAnalytics();
  }, [statusFilter]);

  const fetchReturns = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/returns?limit=50`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setReturns(response.data.returns || []);
    } catch (error) {
      console.error('Failed to fetch returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/returns/analytics/summary`, {
        headers: getAuthHeaders()
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const handleDecision = async (approved) => {
    if (!selectedReturn) return;
    
    try {
      await axios.put(
        `${API}/admin/returns/${selectedReturn._id}/decision`,
        {
          approved,
          notes: decisionNotes,
          refund_amount: approved ? selectedReturn.estimated_refund : null
        },
        { headers: getAuthHeaders() }
      );
      setShowDetailModal(false);
      setDecisionNotes('');
      fetchReturns();
      fetchAnalytics();
    } catch (error) {
      console.error('Failed to process return decision:', error);
    }
  };

  const updateReturnStatus = async (returnId, status) => {
    try {
      await axios.put(
        `${API}/admin/returns/${returnId}/status?status=${status}`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchReturns();
    } catch (error) {
      console.error('Failed to update return status:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      requested: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      denied: 'bg-red-100 text-red-800',
      in_transit: 'bg-purple-100 text-purple-800',
      received: 'bg-indigo-100 text-indigo-800',
      inspected: 'bg-cyan-100 text-cyan-800',
      refunded: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const returnStatuses = [
    'requested', 'approved', 'denied', 'in_transit', 
    'received', 'inspected', 'refunded'
  ];

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-returns-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-black text-stone-900">Returns</h1>
          <p className="text-stone-500">Manage return requests and refunds</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Total Returns</p>
                  <p className="text-2xl font-black">{analytics.total_returns || 0}</p>
                </div>
                <RotateCcw className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg bg-yellow-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700">Pending Review</p>
                  <p className="text-2xl font-black text-yellow-800">
                    {analytics.by_status?.requested || 0}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg bg-purple-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700">In Transit</p>
                  <p className="text-2xl font-black text-purple-800">
                    {analytics.by_status?.in_transit || 0}
                  </p>
                </div>
                <Package className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">Refunded</p>
                  <p className="text-2xl font-black text-green-800">
                    {analytics.by_status?.refunded || 0}
                  </p>
                </div>
                <Check className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="mb-6 border-0 shadow-lg">
          <CardContent className="p-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]" data-testid="returns-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Returns</SelectItem>
                {returnStatuses.map(status => (
                  <SelectItem key={status} value={status}>
                    {status.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Returns Table */}
        <Card className="border-0 shadow-lg" data-testid="returns-table">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              </div>
            ) : returns.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <RotateCcw className="h-12 w-12 mx-auto mb-4 text-stone-300" />
                <p>No return requests found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">RMA #</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Order</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Reason</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Items</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {returns.map((ret) => (
                      <tr key={ret._id} className="hover:bg-stone-50">
                        <td className="px-6 py-4 font-mono text-sm">
                          {ret.rma_number || ret._id.slice(-8).toUpperCase()}
                        </td>
                        <td className="px-6 py-4 text-stone-600">{ret.order_number || ret.order_id?.slice(-8)}</td>
                        <td className="px-6 py-4 text-stone-600 capitalize">{ret.reason?.replace('_', ' ')}</td>
                        <td className="px-6 py-4">{ret.items?.length || 0} items</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(ret.status)}`}>
                            {ret.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedReturn(ret);
                                setShowDetailModal(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {ret.status === 'requested' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-green-600"
                                  onClick={() => {
                                    setSelectedReturn(ret);
                                    setDecisionNotes('');
                                    setShowDetailModal(true);
                                  }}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-600"
                                  onClick={() => {
                                    setSelectedReturn(ret);
                                    handleDecision(false);
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Return Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Return Request Details</DialogTitle>
            </DialogHeader>
            
            {selectedReturn && (
              <div className="space-y-4">
                <div className="p-4 bg-stone-50 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-stone-600">RMA Number</span>
                    <span className="font-mono font-bold">
                      {selectedReturn.rma_number || selectedReturn._id.slice(-8).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-stone-600">Status</span>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedReturn.status)}`}>
                      {selectedReturn.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Reason</span>
                    <span className="capitalize">{selectedReturn.reason?.replace('_', ' ')}</span>
                  </div>
                </div>

                {selectedReturn.description && (
                  <div>
                    <h4 className="font-medium mb-2">Customer Notes</h4>
                    <p className="text-sm text-stone-600 p-3 bg-stone-50 rounded">
                      {selectedReturn.description}
                    </p>
                  </div>
                )}

                {selectedReturn.photos?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Uploaded Photos</h4>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedReturn.photos.map((photo, idx) => (
                        <img 
                          key={idx} 
                          src={photo} 
                          alt={`Return photo ${idx + 1}`}
                          className="w-full h-20 object-cover rounded"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {selectedReturn.status === 'requested' && (
                  <div>
                    <h4 className="font-medium mb-2">Decision Notes</h4>
                    <Textarea
                      value={decisionNotes}
                      onChange={(e) => setDecisionNotes(e.target.value)}
                      placeholder="Add notes for this decision..."
                      rows={3}
                    />
                  </div>
                )}

                {selectedReturn.status === 'approved' && (
                  <div>
                    <h4 className="font-medium mb-2">Update Status</h4>
                    <Select onValueChange={(v) => updateReturnStatus(selectedReturn._id, v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select new status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_transit">In Transit</SelectItem>
                        <SelectItem value="received">Received</SelectItem>
                        <SelectItem value="inspected">Inspected</SelectItem>
                        <SelectItem value="refunded">Refunded</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              {selectedReturn?.status === 'requested' && (
                <>
                  <Button 
                    variant="outline" 
                    className="text-red-600"
                    onClick={() => handleDecision(false)}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Deny Return
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleDecision(true)}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Approve Return
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminReturns;
